const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/selectors/orderSelectors.ts';
import Imm from 'immutable';
import _ from 'lodash';
import { createSelector } from 'reselect';
import { selectCurrentPatientNr } from '../selectors/patientsSelectors';
import { keepRecWithThisKatFkey } from '../utils/orderHotspotEventUtils';
import { CommonStoreState } from '../reducers';
import { IKatFkey, OrderData } from '@cistec/common_web/defs/kisim';

const emptyMap = Imm.Map<string, any>(); // used to avoid creating a new instance every time
const emptyList = Imm.List<any>(); // used to avoid creating a new instance every time

export const ordersLoadingRoot = (state: CommonStoreState) => state.orders.get('loading') || emptyMap;
export const orderRecordsRoot = (state: CommonStoreState) => state.orders.get('records');
export const ordersIndexByPat = (state: CommonStoreState) => state.orders.getIn(['index', 'byPat']) || emptyMap;

/**
 * @return: The number of orders that are being loaded in the context of the current patient.
 */
export const ordersLoadingCountOfCurPat = createSelector(
    [ordersLoadingRoot, selectCurrentPatientNr],
    (_ordersLoadingRoot: Imm.Map<string, number>, _currentPatient: string): number => {
        if (!_ordersLoadingRoot || !_currentPatient) {
            return 0;
        } else {
            return _ordersLoadingRoot.get(_currentPatient) || 0;
        }
    }
);

/**
 * Selector to determines whether orders are currently being loaded
 */
export const isLoadingOrdersOfCurPat = createSelector([ordersLoadingCountOfCurPat], (count: number) => count > 0);

/**
 * @return: A list of orderIds of all the orders belonging to the current patient.
 */
export const ordersIndexOfCurPat = createSelector(
    [selectCurrentPatientNr, ordersIndexByPat],
    (_currentPatient: string, _ordersIndexByPat: Imm.Map<string, any>): Imm.List<string> => {
        if (!_ordersIndexByPat || !_currentPatient) {
            return emptyList;
        } else {
            return _ordersIndexByPat.get(_currentPatient) || emptyList;
        }
    }
);

/**
 * @return: A map (key=orderId, value=record) of the orders belonging to the
 *    current patient. All the orders will be returned, even inactive ones
 *    (i.e. those with STATUS=I).
 */
export const selectOrderRecordsOfCurPat = createSelector(
    [orderRecordsRoot, ordersIndexOfCurPat],
    (_orderRecordsRoot: Imm.Map<string, any>, _ordersIndexOfCurPat: Imm.List<string>): Imm.Map<string, any> => {
        if (!_orderRecordsRoot || !_ordersIndexOfCurPat) {
            return emptyMap;
        } else {
            return _ordersIndexOfCurPat.reduce((result: Imm.Map<string, any>, orderId: string) => {
                return result.set(orderId, _orderRecordsRoot.get(orderId));
            }, emptyMap);
        }
    }
);

export const makeSelectOrderOfCurPat = (sender?: string, orderNr?: string) =>
    createSelector([selectOrderRecordsOfCurPat], (orders): OrderData | undefined => {
        const orderMap = orders?.toJS() || {};
        const order = orderMap[[sender, orderNr].join('_')];
        return order ? (order as OrderData) : undefined;
    });

/**
 * Get the order with the Id SENDER_ORDERNR of the current patient
 * @param {string} orderId
 */
export const orderRecordOfCurPatWithId = (orderId: string) =>
    createSelector(
        [selectOrderRecordsOfCurPat],
        (orders: Imm.Map<string, any>): Imm.Map<string, any> => orders.get(orderId) || emptyMap
    );

/**
 * @return: A selector that will return the current patient's orders filtered
 *    by KAT/FKEY. If FKEY is omitted, the orders are filtered by KAT only.
 *    Orders are always filtered by STAT1, i.e. inactive orders (STAT1=I) and
 *    paused orders (STAT1=P) will NOT be returned by default.
 * @param {IKatFkey[]} katsFkeysToKeep  objects to keep (KATs and FKEYs)
 * @param {boolean} includePaused       if true, paused orders are included in the result
 */
export const orderRecordsOfCurPatWithKatFkey = (katsFkeysToKeep?: IKatFkey[], includePaused = false) =>
    createSelector([selectOrderRecordsOfCurPat], (orderRecordsOfCurPat: Imm.Map<string, any>): Imm.Map<string, any> => {
        const katsFkeys = katsFkeysToKeep;
        if (
            !orderRecordsOfCurPat ||
            !Imm.Map.isMap(orderRecordsOfCurPat) ||
            orderRecordsOfCurPat.isEmpty() ||
            !katsFkeys ||
            _.isEmpty(katsFkeys)
        ) {
            return emptyMap;
        } else {
            return orderRecordsOfCurPat.filter((rec: Imm.Map<string, any>) => {
                return (
                    rec &&
                    rec.get('STAT1') !== 'I' &&
                    (includePaused || rec.get('STAT1') !== 'P') &&
                    keepRecWithThisKatFkey(katsFkeys, rec.get('KAT'), rec.get('FKEY'))
                );
            }) as Imm.Map<string, any>;
        }
    });

/**
 * Helper function to filter orders that are active within a specific time range
 * @param mindat start of time interval (inclusive) for which order records should be included
 * @param maxdat end of time interval (inclusiv) for which order records should be included
 * @param orders Map of orders where the order records are the Map values
 */
const orderTimeRangeFilter = (
    mindat: string,
    maxdat: string,
    orders: Imm.Map<string, Imm.Map<string, string>>
): Imm.Map<string, any> =>
    orders.filter(
        (order: Imm.Map<string, any>) =>
            (order.get('STARTDAT') <= mindat && order.get('STOPDAT') >= maxdat) ||
            (order.get('STARTDAT') >= mindat && order.get('STARTDAT') < maxdat) ||
            (order.get('STOPDAT') >= mindat && order.get('STOPDAT') < maxdat)
    ) as Imm.Map<string, any>;

/**
 * Get orders of the current patient which are within a certain date
 * @param {string} mindat   minimum date of the time range in which the order must be
 * @param {string} maxdat   maximum date of the time range in which the order must be
 */
export const ordersOfCurPatInTimeRange = (mindat: string, maxdat: string) =>
    createSelector([selectOrderRecordsOfCurPat], (orders: Imm.Map<string, any>): Imm.Map<string, any> => {
        if (!orders) {
            return emptyMap;
        }
        return orderTimeRangeFilter(mindat, maxdat, orders);
    });

/**
 * Get orders of the current patient which are within a certain date and for the given KATs and FKEYs
 * @param {string} mindat   minimum date of the time range in which the order must be
 * @param {string} maxdat   maximum date of the time range in which the order must be
 * @param {IKatFkey[]} katsFkeysToKeep  objects to keep (KATs and FKEYs)
 * @param {boolean} includePaused       if true, paused orders are included in the result
 */
export const ordersOfCurPatWithKatFkeyTime = (
    mindat: string,
    maxdat: string,
    katsFkeysToKeep?: IKatFkey[],
    includePaused = false
) =>
    createSelector(
        [orderRecordsOfCurPatWithKatFkey(katsFkeysToKeep, includePaused)],
        (orders: Imm.Map<string, any>): Imm.Map<string, any> => {
            if (!orders) {
                return emptyMap;
            }
            return orderTimeRangeFilter(mindat, maxdat, orders);
        }
    );

/**
 * Retrieves all medication orders of the current patient
 * Note: building fails if this is in patapp_web \_(-.-)_/
 */
export const mediOrdersOfCurPat = orderRecordsOfCurPatWithKatFkey([{ KAT: 'MEDI' }], true);
